/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import { useFollowingQuestions } from '../../../../MedicalHistory/MedicalHistory/utils/questions';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { sanitizeMedicalHistoryBooleans } from '../utils/medicalHistoryDefaults';
import styles from './styles.module.scss';

const ClinicalDataStepSix = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const [loading, setLoading] = useState(true);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [usingSubstances, setUsingSubstances] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the patient actually edited this step, so we only confirm a
  // real save instead of showing a misleading toast on plain navigation.
  const userEditedRef = useRef(false);

  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: null,
    conditions_treated: null,
    taking_medication: null,
    medications: null,
    allergic_to_medications: null,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  });

  const expandedUseFollowingQuestions = [
    ...useFollowingQuestions,
    {
      id: 'none_of_the_above',
      title: 'None of the above',
      description: null,
      is_required: false,
      is_conditional: false,
      type: null,
      options: null,
    },
  ];

  const saveFormData = useCallback((): void => {
    if (hasUnsavedChanges && !loading) {
      sessionStorage.setItem('substances', JSON.stringify({ usingSubstances }));
    }
  }, [hasUnsavedChanges, loading, usingSubstances]);

  const onSubmit = async (): Promise<void> => {
    if (bypassRequired && usingSubstances.length === 0) {
      sessionStorage.removeItem('substances');
      roleNavigate('/clinical-data/step-eight');
      return;
    }

    try {
      setLoading(true);
      setHasUnsavedChanges(false);
      const convertedUsingSubstances = usingSubstances
        .map((item) => {
          switch (item) {
            case 'tobacco_products':
              return 'Tobacco Products';
            case 'recreational_drugs':
              return 'Recreational Drugs';
            case 'prescription_medication_for_non_medical_reasons':
              return 'Prescription medication for non-medical reasons';
            default:
              return item;
          }
        })
        .filter((item) => item !== 'none_of_the_above');

      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatientId as number,
        ...medicalHistory,
        using_substances: convertedUsingSubstances,
        interested_in_stopping: medicalHistory.interested_in_stopping || null,
      };

      const sanitizedPayload = sanitizeMedicalHistoryBooleans(medicalHistoryPayload, bypassRequired);
      if (!historyObjectId) {
        await addClinicalData('medical_history', sanitizedPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), sanitizedPayload);
      }

      if (userEditedRef.current && !isIntakeProcess) {
        showSnackbar('Medical history saved successfully!', 'success');
      }
      sessionStorage.removeItem('substances');
      setLoading(false);

      if (convertedUsingSubstances.length === 0) {
        roleNavigate('/clinical-data/step-eight');
        return;
      }

      roleNavigate('/clinical-data/step-seven');
    } catch (error) {
      setHasUnsavedChanges(true);
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    let isActive = true;

    const hydrateClinicalData = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const savedDraft = sessionStorage.getItem('substances');
        const res = await listClinicalData(selectedPatientId);

        if (!isActive) return;

        if (res.medical_history) {
          setHistoryObjectId(res.medical_history.id || null);
          if (res.medical_history.using_substances.length > 0) {
            const substances = res.medical_history.using_substances as string[];
            const selected: string[] = [];
            if (substances.includes('Tobacco Products')) selected.push('tobacco_products');
            if (substances.includes('Recreational Drugs')) selected.push('recreational_drugs');
            if (substances.includes('Prescription medication for non-medical reasons')) {
              selected.push('prescription_medication_for_non_medical_reasons');
            }
            setUsingSubstances(selected);
          }
          setMedicalHistory(res.medical_history);
        }
        if (savedDraft) {
          const parsedData = JSON.parse(savedDraft);
          setUsingSubstances(parsedData.usingSubstances);
        }
      } catch (error) {
        if (isActive) {
          displayErrorDetails(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
          setHasUnsavedChanges(true);
        }
      }
    };

    hydrateClinicalData();

    return () => {
      isActive = false;
    };
  }, [selectedPatientId]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) return <Loading fullScreen />;

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={40} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Do you use any of the following?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                {expandedUseFollowingQuestions.map((element) => {
                  const isSelected = usingSubstances.includes(element.id);
                  const handleClick = (): void => {
                    userEditedRef.current = true;
                    const nextValue = !isSelected;
                    if (element.id === 'none_of_the_above') {
                      setUsingSubstances(nextValue ? ['none_of_the_above'] : []);
                    } else {
                      setUsingSubstances((prev) => {
                        const filtered = prev.filter((item) => item !== 'none_of_the_above');
                        return nextValue
                          ? filtered.includes(element.id)
                            ? filtered
                            : [...filtered, element.id]
                          : filtered.filter((item) => item !== element.id);
                      });
                    }
                  };
                  return (
                    <div
                      key={element.id}
                      className={`${styles.row} row fs-exclude ${styles.medicalFormRow} ${
                        isSelected ? styles.selectClass : ''
                      }`}
                      onClick={handleClick}
                    >
                      <div className={styles.questionAndButtonContainer}>
                        <div className={`col-6 col-sm-3 px-0 ${styles.select}`}>
                          <div className={styles.check}>
                            <input
                              className={`form-check-input fs-exclude ${styles.checkInput} mt-0 ${
                                isSelected ? styles.primaryColor : ''
                              }`}
                              type="checkbox"
                              name={element.id}
                              id={element.id}
                              checked={isSelected}
                              readOnly
                            />
                          </div>
                        </div>
                        <div className={`${styles.question} col-12 col-sm-8 px-0`}>
                          <div className={`${styles.label} fs-unmask`}>{element.title}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon /> Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || (usingSubstances.length === 0 && !bypassRequired)}
                onClick={(e) => {
                  e.preventDefault();
                  usingSubstances.length || bypassRequired
                    ? onSubmit()
                    : showSnackbar('Please fill in all required fields before proceeding.', 'error');
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};

export default ClinicalDataStepSix;
